import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Collapse,
    Space,
    Typography,
    message,
    Spin,
    Divider,
    Tabs,
    Empty,
    Alert,
    Tag,
    Checkbox,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, TableOutlined } from '@ant-design/icons';
import type {
    EvalGroundTruthRequest,
    EvalGroundTruthResponse,
    EvalGroundTruthUpdateRequest,
    EvalFromChatDraft,
    EvalFixtureRow,
    EvalFixtureSummary,
    EvalFixtureStatus,
    EvalGroundTruthFixturesRefreshRequest,
    EvalGroundTruthFixturesRefreshResponse,
    EvalStaleFixtureRef,
    GroundTruthPlanItem,
    GroundTruthPerTaskItem,
    GroundTruthVizItem,
    GroundTruthHITL,
    TaskResultSqlOutput,
    QuestionSetInfo,
    GroundTruthComment,
} from './evalTypes';
import {
    INTENT_OPTIONS,
    STATUS_OPTIONS,
    CHART_TYPE_OPTIONS,
    type GroundTruthMetadata,
} from './evalTypes';
import type { Tenant, FileEntry } from '../prompts/types';
import { parseTableData } from './parseTableData';
import SkillStudioVizPreview from './SkillStudioVizPreview';
import SkillStudioEditableTable from './SkillStudioEditableTable';

const { TextArea } = Input;
const { Text } = Typography;

export type FormMode = 'create' | 'edit' | 'from_chat';

export interface SkillStudioEvalGroundTruthSubmitPayload {
    mode: FormMode;
    entryId?: string;
    body: EvalGroundTruthRequest | EvalGroundTruthUpdateRequest;
    metadata?: GroundTruthMetadata;
}

export interface SkillStudioEvalGroundTruthFormProps {
    open: boolean;
    mode: FormMode;
    initialData?: EvalFromChatDraft | EvalGroundTruthResponse | null;
    entryId?: string;
    onClose: () => void;
    onSaved?: (entry: EvalGroundTruthResponse) => void;
    questionSets: QuestionSetInfo[];
    tenants: Tenant[];
    taskFileEntries: FileEntry[];
    onTenantChange: (tenant: string) => void;
    saving?: boolean;
    onSubmit: (payload: SkillStudioEvalGroundTruthSubmitPayload) => Promise<EvalGroundTruthResponse>;
    onAddComment?: (entryId: string, text: string) => Promise<EvalGroundTruthResponse>;
    onLoadFixtures?: (threadId: string, runId: string) => Promise<EvalFixtureRow[]>;
    /** Load GT-owned fixtures for an existing entry (preferred for edit mode). */
    onLoadEntryFixtures?: (entryId: string) => Promise<EvalFixtureRow[]>;
    /** Rebuild/recopy selected GT fixtures (Preview/Apply via dry_run). */
    onRefreshEntryFixtures?: (
        entryId: string,
        body: EvalGroundTruthFixturesRefreshRequest,
    ) => Promise<EvalGroundTruthFixturesRefreshResponse>;
    /** Open on Fixtures tab (e.g. post-regen review CTA). */
    initialTab?: 'ground-truth' | 'fixtures';
}

// --- Per-task output UI state ---

interface PerTaskUIState {
    taskId: string;
    task: string;
    subQuery: string;
    sqlQueries: string[];
    sqlTables: TaskResultSqlOutput[];
    pasteTexts: string[];
    narrative: string;
    keyTakeaways: string;
    assumptions: string;
    formulae: string;
    chartType: string;
    chartTitle: string;
    /** Chart viz object (non-table) — for Visualization tab */
    vizRaw: GroundTruthVizItem | null;
    /** Table viz object — for Table tab alongside sql_db_output */
    tableVizRaw: GroundTruthVizItem | null;
}

interface PlanFormItem {
    task_id?: string;
    task?: string;
    sub_query?: string;
}

interface HitlFormItem {
    name?: string;
    expected?: boolean;
    scripted_response?: string;
    should_use_value?: string;
    should_complete?: boolean;
}

const HITL_AGENT_OPTIONS = [
    { value: 'planner', label: 'Planner' },
    { value: 'refine_agent', label: 'Refine Agent' },
] as const;
const HITL_AGENT_KEYS = new Set<string>(HITL_AGENT_OPTIONS.map((o) => o.value));

function normalizeTaskNameForId(taskName: string): string {
    return taskName
        .trim()
        .replace(/[^a-zA-Z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase() || 'task';
}

function fallbackUuidV4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

function generateTaskId(taskName: string): string {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : fallbackUuidV4();
    return `${normalizeTaskNameForId(taskName)}_${uuid.replace(/-/g, '').slice(0, 8)}`;
}

function ensureTaskId(existingId: string | undefined, taskName: string | undefined): string {
    const trimmedId = existingId?.trim();
    if (trimmedId) return trimmedId;
    const trimmedTaskName = taskName?.trim();
    return trimmedTaskName ? generateTaskId(trimmedTaskName) : '';
}

function findPlanStepForPerTask(
    planSteps: GroundTruthPlanItem[],
    taskId: string,
    task: string,
): GroundTruthPlanItem | undefined {
    if (taskId) return planSteps.find((p) => p.task_id === taskId);
    const taskMatches = planSteps.filter((p) => p.task === task);
    return taskMatches.length === 1 ? taskMatches[0] : undefined;
}

function emptyPerTask(task = '', subQuery = '', taskId = ''): PerTaskUIState {
    return {
        taskId,
        task, subQuery,
        sqlQueries: [], sqlTables: [], pasteTexts: [],
        narrative: '', keyTakeaways: '', assumptions: '', formulae: '',
        chartType: '', chartTitle: '',
        vizRaw: null, tableVizRaw: null,
    };
}

/**
 * Normalize sql_db_output: if rows are 2D arrays (standard SQL format),
 * convert them to column-keyed objects so Ant Design Table can render them.
 */
function normalizeSqlOutput(tables: TaskResultSqlOutput[]): TaskResultSqlOutput[] {
    return tables.map((t) => {
        if (!t.rows || t.rows.length === 0 || !t.columns || t.columns.length === 0) return t;
        const firstRow = t.rows[0];
        if (Array.isArray(firstRow)) {
            return {
                columns: t.columns,
                rows: (t.rows as unknown as unknown[][]).map((rowArr) => {
                    const obj: Record<string, unknown> = {};
                    t.columns.forEach((col, idx) => {
                        obj[col] = rowArr[idx] ?? null;
                    });
                    return obj;
                }),
            };
        }
        return t;
    });
}

/**
 * Split viz (single or array) into table-type and chart-type viz objects.
 */
function splitViz(viz: GroundTruthVizItem | GroundTruthVizItem[] | undefined): {
    chartViz: GroundTruthVizItem | null;
    tableViz: GroundTruthVizItem | null;
} {
    if (!viz) return { chartViz: null, tableViz: null };
    const list = Array.isArray(viz) ? viz : [viz];
    const tableViz = list.find((v) => String(v.chart_type ?? '').toLowerCase() === 'table') ?? null;
    const chartViz = list.find((v) => String(v.chart_type ?? '').toLowerCase() !== 'table') ?? null;
    return { chartViz, tableViz };
}

/**
 * Convert a table-type viz (2D data array) into TaskResultSqlOutput.
 */
function tableVizToSqlOutput(viz: GroundTruthVizItem): TaskResultSqlOutput | null {
    const data = (viz as Record<string, unknown>).data;
    if (!Array.isArray(data) || data.length < 2) return null;
    const headers = (data[0] as unknown[]).map(String);
    const rows = data.slice(1).map((row: unknown[]) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((h, i) => { obj[h] = Array.isArray(row) && i < row.length ? row[i] ?? null : null; });
        return obj;
    });
    return { columns: headers, rows };
}

function perTaskItemToUI(item: GroundTruthPerTaskItem): PerTaskUIState {
    const tables = normalizeSqlOutput(item.sql_db_output ?? []);
    const { chartViz, tableViz } = splitViz(item.viz);

    // Merge tableVizRaw into sqlTables so everything is editable in one list
    const allTables = [...tables];
    if (tableViz) {
        const converted = tableVizToSqlOutput(tableViz);
        if (converted && converted.columns.length > 0) allTables.unshift(converted);
    }

    return {
        taskId: item.task_id ?? '',
        task: item.task,
        subQuery: item.sub_query ?? '',
        sqlQueries: item.sql_queries ?? [],
        sqlTables: allTables,
        pasteTexts: allTables.map(() => ''),
        narrative: item.narrative?.overview ?? '',
        keyTakeaways: (item.narrative?.key_takeaways ?? []).join('\n'),
        assumptions: (item.narrative?.assumptions ?? []).join('\n'),
        formulae: (item.narrative?.formulae ?? []).join('\n'),
        chartType: chartViz?.chart_type ?? '',
        chartTitle: chartViz?.chartTitle ?? '',
        vizRaw: chartViz,
        tableVizRaw: null,
    };
}

function uiToPerTaskItem(ui: PerTaskUIState): GroundTruthPerTaskItem {
    const item: GroundTruthPerTaskItem = { task: ui.task };
    const taskId = ensureTaskId(ui.taskId, ui.task);
    if (taskId) item.task_id = taskId;
    if (ui.subQuery) item.sub_query = ui.subQuery;
    const queries = ui.sqlQueries.filter((q) => q.trim());
    if (queries.length) item.sql_queries = queries;
    const tables = ui.sqlTables.filter((t) => t.columns?.length > 0 && t.rows?.length > 0);
    if (tables.length) item.sql_db_output = tables;
    if (ui.chartType || ui.vizRaw) {
        item.viz = {
            ...(ui.vizRaw ?? { chart_type: '', chartTitle: '' }),
            chart_type: ui.chartType || ui.vizRaw?.chart_type || '',
            chartTitle: ui.chartTitle || ui.vizRaw?.chartTitle || '',
        };
    }
    const overview = ui.narrative.trim();
    const takeaways = ui.keyTakeaways.split('\n').map((s) => s.trim()).filter(Boolean);
    const assumptions = ui.assumptions.split('\n').map((s) => s.trim()).filter(Boolean);
    const formulae = ui.formulae.split('\n').map((s) => s.trim()).filter(Boolean);
    if (overview || takeaways.length || assumptions.length || formulae.length) {
        item.narrative = {};
        if (overview) item.narrative.overview = overview;
        if (takeaways.length) item.narrative.key_takeaways = takeaways;
        if (assumptions.length) item.narrative.assumptions = assumptions;
        if (formulae.length) item.narrative.formulae = formulae;
    }
    return item;
}

function linesToList(value: string | undefined): string[] {
    return (value ?? '').split('\n').map((s) => s.trim()).filter(Boolean);
}

function hitlToFormItems(hitl: GroundTruthHITL | null | undefined): HitlFormItem[] {
    if (!hitl) return [];
    return Object.entries(hitl).filter(([name]) => HITL_AGENT_KEYS.has(name)).map(([name, config]) => {
        const validation = config.validation ?? {};
        const scriptedResponses = config.scripted_responses ?? [];
        return {
            name,
            expected: config.expected,
            scripted_response: config.scripted_response ?? scriptedResponses.join('\n'),
            should_use_value: validation.should_use_value == null ? '' : String(validation.should_use_value),
            should_complete: validation.should_complete,
        };
    });
}

function formItemsToHitl(items: HitlFormItem[] | undefined): GroundTruthHITL | undefined {
    const hitl = (items ?? []).reduce<GroundTruthHITL>((acc, item) => {
        const name = item.name?.trim();
        if (!name) return acc;
        if (!HITL_AGENT_KEYS.has(name)) return acc;
        const scriptedResponses = linesToList(item.scripted_response);
        const validation: Record<string, unknown> = {};
        if (item.should_use_value?.trim()) validation.should_use_value = item.should_use_value.trim();
        if (item.should_complete != null) validation.should_complete = item.should_complete;
        acc[name] = {
            ...(item.expected != null ? { expected: item.expected } : {}),
            ...(scriptedResponses.length === 1 ? { scripted_response: scriptedResponses[0] } : {}),
            ...(scriptedResponses.length > 1 ? { scripted_responses: scriptedResponses } : {}),
            ...(Object.keys(validation).length > 0 ? { validation } : {}),
        };
        return acc;
    }, {});
    return Object.keys(hitl).length > 0 ? hitl : undefined;
}

function findInvalidHitl(items: HitlFormItem[] | undefined): string | null {
    const invalid = (items ?? []).find((item) => (
        item.expected === true
        && linesToList(item.scripted_response).length === 0
    ));
    return invalid?.name?.trim() || (invalid ? 'HITL entry' : null);
}

function formatCommentTimestamp(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function getFixtureTaskId(fixture: EvalFixtureRow | EvalFixtureSummary): string | null | undefined {
    const metadata = 'metadata' in fixture && fixture.metadata && typeof fixture.metadata === 'object'
        ? fixture.metadata as { task_id?: string | null }
        : null;
    if (metadata?.task_id) return metadata.task_id;
    const taskId = (fixture as EvalFixtureSummary).task_id;
    return typeof taskId === 'string' ? taskId : taskId === null ? null : undefined;
}

function getFixtureLogicalAgent(fixture: EvalFixtureRow | EvalFixtureSummary): string | undefined {
    const metadata = 'metadata' in fixture && fixture.metadata && typeof fixture.metadata === 'object'
        ? fixture.metadata as { logical_agent?: string }
        : null;
    if (metadata?.logical_agent) return metadata.logical_agent;
    const logicalAgent = (fixture as EvalFixtureSummary).logical_agent;
    if (typeof logicalAgent === 'string') return logicalAgent;
    return typeof fixture.agent_name === 'string' ? fixture.agent_name.split('.')[0] : undefined;
}

function getFixtureField(fixture: EvalFixtureRow | EvalFixtureSummary, key: string): unknown {
    if (key in fixture) return (fixture as Record<string, unknown>)[key];
    const metadata = 'metadata' in fixture && fixture.metadata && typeof fixture.metadata === 'object'
        ? fixture.metadata as Record<string, unknown>
        : {};
    return metadata[key];
}

function getFixtureStatus(fixture: EvalFixtureRow | EvalFixtureSummary): EvalFixtureStatus | undefined {
    const status = getFixtureField(fixture, 'status');
    return status === 'available' || status === 'stale' ? status : undefined;
}

function getFixtureNumericId(fixture: EvalFixtureRow | EvalFixtureSummary | EvalStaleFixtureRef): string | undefined {
    const id = fixture.fixture_id ?? fixture.id;
    return id != null ? String(id) : undefined;
}

function formatRemainingStale(
    remaining: EvalGroundTruthFixturesRefreshResponse['stale_fixtures_remaining'],
): string {
    if (!remaining || !Array.isArray(remaining) || remaining.length === 0) return '';
    return remaining.map((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        const id = getFixtureNumericId(item);
        const agent = item.logical_agent || item.agent_name || 'fixture';
        return id ? `${agent} (#${id})` : String(agent);
    }).join(', ');
}

function normalizeRegenerationField(value: string): string {
    // Strip task-scoped selectors: GroundTruthPerTask[task_id].viz → GroundTruthPerTask.viz
    return value.replace(/\[[^\]]*]/g, '').toLowerCase();
}

function regenerationFieldTaskId(field: string): string | null {
    const match = field.match(/^GroundTruthPerTask\[([^\]]+)\]/i);
    return match?.[1] ?? null;
}

function hasRegenerationField(
    fields: string[],
    aliases: string[],
    options?: { taskId?: string },
): boolean {
    const taskId = options?.taskId;
    return fields.some((field) => {
        const fieldTaskId = regenerationFieldTaskId(field);
        if (taskId && fieldTaskId && fieldTaskId !== taskId) return false;

        const normalizedField = normalizeRegenerationField(field);
        return aliases.some((alias) => {
            const normalizedAlias = normalizeRegenerationField(alias);
            return normalizedField === normalizedAlias
                || normalizedField.startsWith(`${normalizedAlias}.`)
                || normalizedAlias.startsWith(`${normalizedField}.`);
        });
    });
}

// ---- Component ----

const SkillStudioEvalGroundTruthForm: React.FC<SkillStudioEvalGroundTruthFormProps> = ({
    open,
    mode,
    initialData,
    entryId,
    onClose,
    onSaved,
    questionSets,
    tenants,
    taskFileEntries,
    onTenantChange,
    saving: savingProp,
    onSubmit,
    onAddComment,
    onLoadFixtures,
    onLoadEntryFixtures,
    onRefreshEntryFixtures,
    initialTab,
}) => {
    const [form] = Form.useForm();
    const [savingLocal, setSavingLocal] = useState(false);
    const saving = savingProp ?? savingLocal;
    const [commentText, setCommentText] = useState('');
    const [pendingComments, setPendingComments] = useState<string[]>([]);
    const [addingComment, setAddingComment] = useState(false);
    const [loadedFixtures, setLoadedFixtures] = useState<EvalFixtureRow[]>([]);
    const [fixturesLoaded, setFixturesLoaded] = useState(false);
    const [fixturesLoading, setFixturesLoading] = useState(false);
    const [selectedFixtureByAgent, setSelectedFixtureByAgent] = useState<Record<string, string>>({});
    const [fixturesTabVisited, setFixturesTabVisited] = useState(false);
    const [activeTab, setActiveTab] = useState<'ground-truth' | 'fixtures' | 'comments'>('ground-truth');
    const [refreshFixtureIds, setRefreshFixtureIds] = useState<string[]>([]);
    const [fixturesRefreshing, setFixturesRefreshing] = useState(false);
    const [fixtureRefreshResult, setFixtureRefreshResult] = useState<EvalGroundTruthFixturesRefreshResponse | null>(null);
    const [fixtureRefreshWasPreview, setFixtureRefreshWasPreview] = useState(false);

    const [selectedTenant, setSelectedTenant] = useState<string>('');

    // Per-task outputs (separate from Ant Form)
    const [perTaskList, setPerTaskList] = useState<PerTaskUIState[]>([]);

    // --- Populate form from initialData ---
    useEffect(() => {
        if (open && initialData) {
            const gt = initialData.ground_truth ?? {};
            const planSteps: GroundTruthPlanItem[] = (gt.GroundTruthPlan ?? []).map((p) => {
                const taskId = ensureTaskId(p.task_id, p.task);
                return {
                    task: p.task,
                    sub_query: p.sub_query,
                    ...(taskId ? { task_id: taskId } : {}),
                };
            });
            form.setFieldsValue({
                question_set: initialData.question_set ? [initialData.question_set] : [],
                question: initialData.question,
                intent: gt.GroundTruthIntent,
                refine: gt.GroundTruthRefine,
                tasks: gt.GroundTruthTasks ?? [],
                plan: planSteps.map((p) => ({
                    task_id: p.task_id,
                    task: p.task || undefined,
                    sub_query: p.sub_query,
                })),
                narrative_overview: gt.GroundTruthNarrative?.overview,
                narrative_key_takeaways: (gt.GroundTruthNarrative?.key_takeaways ?? []).join('\n'),
                hitl: hitlToFormItems(gt.GroundTruthHITL ?? initialData.hitl_test),
                status: initialData.status ?? 'draft',
            });
            setPerTaskList((gt.GroundTruthPerTask ?? []).map((item) => {
                const ui = perTaskItemToUI(item);
                const match = findPlanStepForPerTask(planSteps, ui.taskId, ui.task);
                if (!ui.taskId && match?.task_id) ui.taskId = match.task_id;
                if (!ui.subQuery && match) {
                    if (match?.sub_query) ui.subQuery = match.sub_query;
                }
                ui.taskId = ensureTaskId(ui.taskId, ui.task);
                return ui;
            }));
            setCommentText('');
            setPendingComments([]);
            setLoadedFixtures([]);
            setFixturesLoaded(false);
            setSelectedFixtureByAgent({});
            setFixturesTabVisited(false);
            setFixtureRefreshResult(null);
            setFixtureRefreshWasPreview(false);
            setRefreshFixtureIds([]);
        } else if (open) {
            form.resetFields();
            form.setFieldsValue({ status: 'draft', plan: [], tasks: [], hitl: [] });
            setPerTaskList([]);
            setCommentText('');
            setPendingComments([]);
            setLoadedFixtures([]);
            setFixturesLoaded(false);
            setSelectedFixtureByAgent({});
            setFixturesTabVisited(false);
            setFixtureRefreshResult(null);
            setFixtureRefreshWasPreview(false);
            setRefreshFixtureIds([]);
        }
    }, [open, initialData, form]);

    useEffect(() => {
        if (!open) return;
        setActiveTab(initialTab === 'fixtures' ? 'fixtures' : 'ground-truth');
    }, [open, initialTab]);

    const taskNameOptions = useMemo(
        () => taskFileEntries.map((f) => {
            const raw = f.filename || f.file_id;
            const label = raw.replace(/\.ya?ml$/i, '');
            return { value: label, label };
        }),
        [taskFileEntries],
    );

    // --- Per-task helpers ---

    const updatePerTask = useCallback((idx: number, patch: Partial<PerTaskUIState>) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            next[idx] = { ...(next[idx] ?? emptyPerTask()), ...patch };
            return next;
        });
    }, []);

    const handlePerTaskTaskChange = useCallback((idx: number, task: string) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const current = next[idx] ?? emptyPerTask();
            next[idx] = {
                ...current,
                task,
                taskId: ensureTaskId(current.taskId, task),
            };
            return next;
        });
    }, []);

    const addPerTask = useCallback(() => {
        setPerTaskList((prev) => [...prev, emptyPerTask()]);
    }, []);

    const removePerTask = useCallback((idx: number) => {
        setPerTaskList((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const handleAddTable = useCallback((taskIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlTables = [...t.sqlTables, { columns: [], rows: [] }];
            t.pasteTexts = [...t.pasteTexts, ''];
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleRemoveTable = useCallback((taskIdx: number, tblIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlTables = t.sqlTables.filter((_, i) => i !== tblIdx);
            t.pasteTexts = t.pasteTexts.filter((_, i) => i !== tblIdx);
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handlePasteTextChange = useCallback((taskIdx: number, tblIdx: number, val: string) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const texts = [...t.pasteTexts];
            texts[tblIdx] = val;
            t.pasteTexts = texts;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleParseTable = useCallback((taskIdx: number, tblIdx: number, text: string) => {
        const parsed = parseTableData(text);
        if (!parsed) { message.warning('Could not parse table data — check format'); return; }
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const tables = [...t.sqlTables];
            tables[tblIdx] = parsed;
            t.sqlTables = tables;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleTableEdit = useCallback((taskIdx: number, tblIdx: number, updated: TaskResultSqlOutput) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const tables = [...t.sqlTables];
            tables[tblIdx] = updated;
            t.sqlTables = tables;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleAddSqlQuery = useCallback((taskIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlQueries = [...t.sqlQueries, ''];
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleSqlQueryChange = useCallback((taskIdx: number, qIdx: number, val: string) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const queries = [...t.sqlQueries];
            queries[qIdx] = val;
            t.sqlQueries = queries;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleRemoveSqlQuery = useCallback((taskIdx: number, qIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlQueries = t.sqlQueries.filter((_, i) => i !== qIdx);
            next[taskIdx] = t;
            return next;
        });
    }, []);

    // --- Plan step helpers ---

    const handleAddPlanStep = useCallback((addFn: () => void) => { addFn(); }, []);
    const handleRemovePlanStep = useCallback((removeFn: (idx: number) => void, idx: number) => { removeFn(idx); }, []);
    const handlePlanTaskChange = useCallback((idx: number, task: string) => {
        const plan = (form.getFieldValue('plan') ?? []) as PlanFormItem[];
        const current = plan[idx] ?? {};
        form.setFieldValue(['plan', idx], {
            ...current,
            task,
            task_id: ensureTaskId(current.task_id, task),
        });
    }, [form]);

    // --- Save ---

    const handleSave = async () => {
        try {
            const raw = await form.validateFields();
            if (savingProp == null) setSavingLocal(true);

            const questionSet = Array.isArray(raw.question_set) ? raw.question_set[0] : raw.question_set;
            const values = { ...raw, question_set: questionSet };

            const planSteps: GroundTruthPlanItem[] = (values.plan ?? [])
                .map((p: PlanFormItem) => {
                    if (!p.task && !p.sub_query) return null;
                    const taskId = ensureTaskId(p.task_id, p.task);
                    return {
                        task: p.task ?? '',
                        sub_query: p.sub_query ?? '',
                        ...(taskId ? { task_id: taskId } : {}),
                    };
                })
                .filter(Boolean) as GroundTruthPlanItem[];

            const perTask = perTaskList
                .filter((t) => t.task.trim())
                .map(uiToPerTaskItem);
            const invalidHitl = findInvalidHitl(values.hitl);
            if (invalidHitl) {
                message.error(`${invalidHitl}: scripted response is required when expected is true`);
                return;
            }
            const narrativeTakeaways = linesToList(values.narrative_key_takeaways);
            const structuredNarrative = values.narrative_overview?.trim() || narrativeTakeaways.length
                ? {
                    ...(values.narrative_overview?.trim() ? { overview: values.narrative_overview.trim() } : {}),
                    ...(narrativeTakeaways.length ? { key_takeaways: narrativeTakeaways } : {}),
                }
                : undefined;
            const hitl = formItemsToHitl(values.hitl);

            const groundTruth: EvalGroundTruthRequest['ground_truth'] = {
                GroundTruthIntent: values.intent,
                GroundTruthRefine: values.refine,
                GroundTruthTasks: values.tasks,
                GroundTruthPlan: planSteps,
                GroundTruthNarrative: structuredNarrative,
                GroundTruthHITL: hitl,
                GroundTruthPerTask: perTask.length > 0 ? perTask : undefined,
            };
            const initialCommentText = !entryId
                ? [...pendingComments, commentText.trim()].filter(Boolean).join('\n\n')
                : '';
            const initialComment = initialCommentText
                ? { text: initialCommentText }
                : undefined;

            const nextStatus = values.status;
            const approvingAfterReview = nextStatus === 'approved';
            const existingMetadata = initialData?.metadata;
            const hasPendingRegenerationReview = Boolean(
                existingMetadata?.last_regeneration || existingMetadata?.last_regeneration_preview,
            );
            // Approving means the reviewer accepted regenerations — drop highlight state.
            const clearedRegenMetadata = approvingAfterReview && hasPendingRegenerationReview
                ? {
                    ...existingMetadata,
                    last_regeneration: null,
                    last_regeneration_preview: null,
                }
                : undefined;

            const saved = await onSubmit(
                mode === 'edit' && entryId
                    ? {
                        mode,
                        entryId,
                        body: {
                            question_set: values.question_set,
                            question: values.question,
                            target_agent: initialData?.target_agent,
                            ground_truth: groundTruth,
                            fixture: initialData?.fixture,
                            status: nextStatus,
                            ...(clearedRegenMetadata ? { metadata: clearedRegenMetadata } : {}),
                        },
                    }
                    : {
                        mode,
                        body: {
                            question_set: values.question_set,
                            question: values.question,
                            target_agent: initialData?.target_agent,
                            ground_truth: groundTruth,
                            fixture: initialData?.fixture,
                            status: nextStatus,
                            initial_comment: initialComment,
                        },
                        metadata: initialData?.metadata,
                    },
            );

            message.success(mode === 'edit' ? 'Entry updated' : 'Entry created');
            onSaved?.(saved);
            if (mode === 'edit' && entryId && onLoadEntryFixtures) {
                // Keep open and show fixtures so save-driven staleness is reviewable.
                setActiveTab('fixtures');
                setFixturesTabVisited(true);
                return;
            }
            onClose();
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'errorFields' in err) return;
            message.error(String(err));
        } finally {
            if (savingProp == null) setSavingLocal(false);
        }
    };

    const title =
        mode === 'from_chat' ? 'Create Ground Truth'
            : mode === 'edit' ? 'Edit Ground Truth'
            : 'New Ground Truth';

    const comments: GroundTruthComment[] = initialData?.comments ?? [];
    const canPostComment = Boolean(entryId && onAddComment);
    const canQueueComment = !entryId;
    const canEditCommentText = canPostComment || canQueueComment;
    const hitlTrace = initialData?.metadata?.hitl_trace ?? [];
    const sourceTurn = initialData?.metadata?.source_turn;
    const fixtureSummaries = initialData?.metadata?.available_fixtures ?? [];
    const visibleFixtures: Array<EvalFixtureRow | EvalFixtureSummary> = loadedFixtures.length > 0
        ? loadedFixtures
        : fixtureSummaries;
    const sourceThreadId = initialData?.metadata?.source_thread_id ?? initialData?.metadata?.source_turn?.thread_id;
    const sourceRunId = initialData?.metadata?.source_run_id ?? initialData?.metadata?.source_turn?.selected_run_id;
    const canLoadFixtures = Boolean(onLoadFixtures && sourceThreadId && sourceRunId);
    const watchedStatus = Form.useWatch('status', form) as string | undefined;
    const suppressRegenHighlights = watchedStatus === 'approved' || initialData?.status === 'approved';
    const actualRegeneration = suppressRegenHighlights
        ? undefined
        : (initialData?.metadata?.last_regeneration ?? undefined);
    const previewRegeneration = suppressRegenHighlights || actualRegeneration
        ? undefined
        : (initialData?.metadata?.last_regeneration_preview ?? undefined);
    const regeneration = actualRegeneration ?? previewRegeneration;
    const isRegenerationPreview = Boolean(previewRegeneration);
    const regeneratedFields = regeneration?.regenerated_fields ?? [];
    const staleFields = regeneration?.stale_fields ?? [];
    const hasRegenerationMetadata = regeneratedFields.length > 0 || staleFields.length > 0;

    const getFieldRegenerationState = (aliases: string[], taskId?: string) => ({
        regenerated: hasRegenerationField(regeneratedFields, aliases, { taskId }),
        stale: hasRegenerationField(staleFields, aliases, { taskId }),
    });

    const getFieldHighlightStyle = (aliases: string[], taskId?: string): React.CSSProperties | undefined => {
        const state = getFieldRegenerationState(aliases, taskId);
        if (state.stale) {
            return {
                borderLeft: '3px solid #faad14',
                background: '#fffbe6',
                padding: 8,
                borderRadius: 4,
            };
        }
        if (state.regenerated) {
            return {
                borderLeft: '3px solid #52c41a',
                background: '#f6ffed',
                padding: 8,
                borderRadius: 4,
            };
        }
        return undefined;
    };

    const renderRegenerationLabel = (label: React.ReactNode, aliases: string[], taskId?: string) => {
        const state = getFieldRegenerationState(aliases, taskId);
        if (!state.regenerated && !state.stale) return label;
        return (
            <Space size={6} wrap>
                <span>{label}</span>
                {state.regenerated && (
                    <Tag color={isRegenerationPreview ? 'processing' : 'success'} style={{ margin: 0 }}>
                        {isRegenerationPreview ? 'Would regenerate' : 'Regenerated'}
                    </Tag>
                )}
                {state.stale && <Tag color="warning" style={{ margin: 0 }}>Needs review</Tag>}
            </Space>
        );
    };

    const renderRegenerationSummary = () => {
        if (!hasRegenerationMetadata) return null;
        return (
            <Alert
                type={staleFields.length > 0 ? 'warning' : 'success'}
                showIcon
                style={{ marginBottom: 12 }}
                message={
                    isRegenerationPreview
                        ? 'Dry-run preview: fields would regenerate'
                        : staleFields.length > 0
                            ? 'Regenerated fields need review'
                            : 'Fields regenerated'
                }
                description={(
                    <Space direction="vertical" size={4}>
                        {regeneratedFields.length > 0 && (
                            <Text style={{ fontSize: 12 }}>
                                {isRegenerationPreview ? 'Would regenerate' : 'Regenerated'}: {regeneratedFields.join(', ')}
                            </Text>
                        )}
                        {staleFields.length > 0 && (
                            <Text style={{ fontSize: 12 }}>
                                Needs review: {staleFields.join(', ')}
                            </Text>
                        )}
                    </Space>
                )}
            />
        );
    };

    const renderHitlTrace = () => {
        if (!hitlTrace.length) return null;
        return (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, marginBottom: 12, background: '#fafafa' }}>
                <Space size={8} wrap style={{ marginBottom: 8 }}>
                    <Text strong>Source HITL Trace (read-only)</Text>
                    {sourceTurn?.completed != null && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {sourceTurn.completed ? 'Completed source turn' : 'Incomplete source turn'}
                        </Text>
                    )}
                </Space>
                {sourceTurn?.canonical_question && (
                    <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Original question</Text>
                        <Text style={{ fontSize: 12 }}>{sourceTurn.canonical_question}</Text>
                    </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {hitlTrace.map((entry, idx) => (
                        <div key={`${entry.run_id ?? 'hitl'}-${idx}`} style={{ borderTop: idx === 0 ? undefined : '1px solid #f0f0f0', paddingTop: idx === 0 ? 0 : 8 }}>
                            <Space size={8} wrap>
                                <Text strong style={{ fontSize: 12 }}>{entry.agent ? entry.agent.replace(/_/g, ' ') : `HITL ${idx + 1}`}</Text>
                                {entry.type && <Text type="secondary" style={{ fontSize: 12 }}>{entry.type.replace(/_/g, ' ')}</Text>}
                            </Space>
                            {entry.content && (
                                <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
                                    {entry.content}
                                </div>
                            )}
                            <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>User response: </Text>
                                <Text style={{ fontSize: 12 }}>{entry.user_response || '-'}</Text>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getFixtureChoiceKey = (fixture: EvalFixtureRow | EvalFixtureSummary, fallbackIdx: number): string => {
        const id = fixture.fixture_id ?? fixture.id;
        if (id != null) return String(id);
        return `${getFixtureLogicalAgent(fixture) ?? 'fixture'}-${getFixtureTaskId(fixture) ?? 'global'}-${fallbackIdx}`;
    };

    const getFixtureChoiceLabel = (fixture: EvalFixtureRow | EvalFixtureSummary): string => {
        const agent = getFixtureLogicalAgent(fixture) || fixture.agent_name || 'fixture';
        const taskId = getFixtureTaskId(fixture);
        const base = taskId ? `${agent} · ${taskId}` : String(agent);
        const id = getFixtureNumericId(fixture);
        return id ? `${base} (#${id})` : base;
    };

    const metadataStaleFixtureIds = useMemo(() => {
        const regen = initialData?.metadata?.last_regeneration;
        const refs = regen?.stale_fixtures ?? [];
        return new Set(
            refs
                .map((ref) => getFixtureNumericId(ref))
                .filter((id): id is string => Boolean(id)),
        );
    }, [initialData?.metadata?.last_regeneration]);

    const applyLoadedFixtureSelection = (rows: EvalFixtureRow[]) => {
        const defaults: Record<string, string> = {};
        const staleIds: string[] = [];
        rows.forEach((fixture, idx) => {
            const agent = getFixtureLogicalAgent(fixture) ?? 'unknown';
            if (!defaults[agent]) defaults[agent] = getFixtureChoiceKey(fixture, idx);
            const id = getFixtureNumericId(fixture);
            const status = getFixtureStatus(fixture);
            if (id && (status === 'stale' || metadataStaleFixtureIds.has(id))) {
                staleIds.push(id);
            }
        });
        setSelectedFixtureByAgent(defaults);
        setRefreshFixtureIds(staleIds);
        setFixtureRefreshResult(null);
        setFixtureRefreshWasPreview(false);
    };

    const loadFixturesForForm = async (force = false) => {
        if ((!force && fixturesLoaded) || fixturesLoading) return;
        setFixturesLoading(true);
        try {
            let rows: EvalFixtureRow[] = [];
            if (entryId && onLoadEntryFixtures) {
                try {
                    rows = await onLoadEntryFixtures(entryId);
                } catch {
                    rows = [];
                }
            }
            if (rows.length === 0 && onLoadFixtures && sourceThreadId && sourceRunId) {
                try {
                    rows = await onLoadFixtures(sourceThreadId, sourceRunId);
                } catch {
                    rows = [];
                }
            }
            setLoadedFixtures(rows);
            setFixturesLoaded(true);
            applyLoadedFixtureSelection(rows);
            if (rows.length === 0 && fixtureSummaries.length === 0) {
                message.info('No fixtures found for this ground truth');
            }
        } finally {
            setFixturesLoading(false);
        }
    };

    useEffect(() => {
        if (!open || initialTab !== 'fixtures') return;
        setActiveTab('fixtures');
        setFixturesTabVisited(true);
        void loadFixturesForForm(true);
        // intentionally only when opening via fixtures CTA
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialTab, entryId]);

    useEffect(() => {
        if (!open || activeTab !== 'fixtures' || fixturesLoaded || fixturesLoading) return;
        void loadFixturesForForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, activeTab, fixturesLoaded, fixturesLoading, initialData]);

    const handleRefreshFixtures = async (dryRun: boolean) => {
        if (!entryId || !onRefreshEntryFixtures) return;
        if (refreshFixtureIds.length === 0) {
            message.warning('Select at least one stale fixture to refresh');
            return;
        }
        setFixturesRefreshing(true);
        try {
            const result = await onRefreshEntryFixtures(entryId, {
                fixture_ids: refreshFixtureIds.map((id) => (/^\d+$/.test(id) ? Number(id) : id)),
                dry_run: dryRun,
            });
            setFixtureRefreshResult(result);
            setFixtureRefreshWasPreview(dryRun);
            if (dryRun) {
                message.success('Fixture refresh preview completed — nothing saved');
            } else {
                message.success('Fixtures refreshed');
                await loadFixturesForForm(true);
            }
            const unsupported = (result.results ?? []).filter(
                (row) => String(row.status ?? '').toLowerCase() === 'unsupported',
            );
            if (unsupported.length > 0) {
                message.warning(
                    `${unsupported.length} fixture(s) unsupported for refresh`,
                );
            }
        } catch (err: unknown) {
            message.error(String(err));
        } finally {
            setFixturesRefreshing(false);
        }
    };

    const renderFixturesTab = () => {
        const coverageStatus = (initialData as EvalGroundTruthResponse | null | undefined)?.fixture_coverage?.status;
        const groups = visibleFixtures.reduce<Record<string, Array<EvalFixtureRow | EvalFixtureSummary>>>((acc, fixture) => {
            const logicalAgent = getFixtureLogicalAgent(fixture) ?? 'unknown';
            acc[logicalAgent] = acc[logicalAgent] ?? [];
            acc[logicalAgent].push(fixture);
            return acc;
        }, {});
        const agents = Object.keys(groups).sort();
        const staleFixtures = visibleFixtures.filter((fixture) => {
            const id = getFixtureNumericId(fixture);
            const status = getFixtureStatus(fixture);
            return status === 'stale' || (id != null && metadataStaleFixtureIds.has(id));
        });
        const staleCount = staleFixtures.length;
        const availableCount = visibleFixtures.filter((f) => getFixtureStatus(f) === 'available').length;
        const refreshOptions = staleFixtures
            .map((fixture) => {
                const id = getFixtureNumericId(fixture);
                if (!id) return null;
                return {
                    value: id,
                    label: getFixtureChoiceLabel(fixture),
                };
            })
            .filter((opt): opt is { value: string; label: string } => Boolean(opt));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Space size={8} wrap>
                    <Text strong>Fixture availability</Text>
                    {typeof coverageStatus === 'string' && (
                        <Tag color={
                            coverageStatus === 'full' ? 'success'
                                : coverageStatus === 'partial' ? 'warning'
                                    : coverageStatus === 'stale' ? 'error'
                                        : 'default'
                        }>
                            {coverageStatus === 'full' ? 'Ready'
                                : coverageStatus === 'partial' ? 'Partial'
                                    : coverageStatus === 'stale' ? 'Coverage: some stale'
                                        : coverageStatus === 'none' ? 'None'
                                            : coverageStatus}
                        </Tag>
                    )}
                    {staleCount > 0 && <Tag color="warning">{staleCount} stale</Tag>}
                    {availableCount > 0 && <Tag color="success">{availableCount} available</Tag>}
                    {visibleFixtures.length > 0 && (
                        <Tag>{visibleFixtures.length} fixture{visibleFixtures.length === 1 ? '' : 's'}</Tag>
                    )}
                    {(onLoadEntryFixtures || canLoadFixtures) && (
                        <Button size="small" loading={fixturesLoading} onClick={() => {
                            void loadFixturesForForm(true);
                        }}>
                            Reload fixtures
                        </Button>
                    )}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Per-fixture status drives refresh — row coverage is only a coarse readiness badge, not “all fixtures stale”.
                </Text>
                {fixturesLoading && <Spin size="small" />}
                {!fixturesLoading && agents.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No fixtures available for this entry" />
                )}
                {agents.map((agent) => {
                    const fixtures = groups[agent];
                    const options = fixtures.map((fixture, idx) => ({
                        value: getFixtureChoiceKey(fixture, idx),
                        label: getFixtureChoiceLabel(fixture),
                    }));
                    const selectedKey = selectedFixtureByAgent[agent] ?? options[0]?.value;
                    const selectedFixture = fixtures.find((fixture, idx) => getFixtureChoiceKey(fixture, idx) === selectedKey)
                        ?? fixtures[0];
                    const payload = selectedFixture && 'fixture' in selectedFixture ? selectedFixture.fixture : null;
                    const selectedStatus = selectedFixture ? getFixtureStatus(selectedFixture) : undefined;
                    const selectedStaleReason = selectedFixture
                        ? getFixtureField(selectedFixture, 'stale_reason')
                        : undefined;
                    return (
                        <div key={agent} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Space size={8} wrap>
                                    <Text strong>{agent}</Text>
                                    {fixtures.some((f) => getFixtureStatus(f) === 'stale') && (
                                        <Tag color="warning">Has stale</Tag>
                                    )}
                                </Space>
                                <Select
                                    style={{ width: '100%' }}
                                    value={selectedKey}
                                    options={options}
                                    onChange={(value) => setSelectedFixtureByAgent((prev) => ({ ...prev, [agent]: value }))}
                                />
                                {selectedFixture && (
                                    <Space size={6} wrap>
                                        {selectedStatus === 'stale' && <Tag color="warning">Stale</Tag>}
                                        {selectedStatus === 'available' && <Tag color="success">Available</Tag>}
                                        {getFixtureTaskId(selectedFixture) && (
                                            <Tag color="blue">task: {getFixtureTaskId(selectedFixture)}</Tag>
                                        )}
                                        {selectedFixture.source_node && (
                                            <Tag>{selectedFixture.source_node}</Tag>
                                        )}
                                        {selectedFixture.capture_stage && (
                                            <Tag>{selectedFixture.capture_stage}</Tag>
                                        )}
                                        {typeof selectedStaleReason === 'string' && selectedStaleReason && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>{selectedStaleReason}</Text>
                                        )}
                                    </Space>
                                )}
                                {payload != null ? (
                                    <pre style={{
                                        fontSize: 11,
                                        margin: 0,
                                        maxHeight: 280,
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        background: '#fafafa',
                                        padding: 8,
                                        borderRadius: 4,
                                    }}>
                                        {JSON.stringify(payload, null, 2)}
                                    </pre>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Summary only — reload fixtures to load full payloads when available.
                                    </Text>
                                )}
                            </Space>
                        </div>
                    );
                })}

                {entryId && onRefreshEntryFixtures && staleCount > 0 && (
                    <div style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 6, padding: 12 }}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Refresh stale fixtures</Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                            Rebuilds narrative/viz fixtures from current GT, or recopies upstream agents from source chat when available.
                        </Text>
                        <Checkbox.Group
                            style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}
                            value={refreshFixtureIds}
                            options={refreshOptions}
                            onChange={(values) => setRefreshFixtureIds(values.map(String))}
                        />
                        <Space>
                            <Button
                                loading={fixturesRefreshing}
                                disabled={refreshFixtureIds.length === 0}
                                onClick={() => void handleRefreshFixtures(true)}
                            >
                                Preview
                            </Button>
                            <Button
                                type="primary"
                                loading={fixturesRefreshing}
                                disabled={refreshFixtureIds.length === 0}
                                onClick={() => void handleRefreshFixtures(false)}
                            >
                                Apply refresh
                            </Button>
                        </Space>
                    </div>
                )}

                {fixtureRefreshWasPreview && (
                    <Alert
                        type="info"
                        showIcon
                        message="Preview only — nothing was saved. Click Apply refresh to persist."
                    />
                )}
                {fixtureRefreshResult?.results?.length ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="Fixture refresh results"
                        description={(
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {fixtureRefreshResult.results.map((row, idx) => (
                                    <li key={`${row.fixture_id ?? row.id ?? idx}`}>
                                        #{row.fixture_id ?? row.id ?? '?'} — {row.status ?? 'unknown'}
                                        {row.reason ? `: ${row.reason}` : ''}
                                    </li>
                                ))}
                            </ul>
                        )}
                    />
                ) : null}
                {fixtureRefreshResult?.stale_fixtures_remaining != null
                    && Array.isArray(fixtureRefreshResult.stale_fixtures_remaining)
                    && fixtureRefreshResult.stale_fixtures_remaining.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Still stale after refresh"
                        description={formatRemainingStale(fixtureRefreshResult.stale_fixtures_remaining)}
                    />
                )}
            </div>
        );
    };

    const handleAddComment = async () => {
        const trimmed = commentText.trim();
        if (!trimmed || !canEditCommentText) return;

        if (canQueueComment) {
            setPendingComments((prev) => [...prev, trimmed]);
            setCommentText('');
            return;
        }

        if (!entryId || !onAddComment) return;
        setAddingComment(true);
        try {
            const updated = await onAddComment(entryId, trimmed);
            setCommentText('');
            message.success('Comment added');
            onSaved?.(updated);
        } catch (err: unknown) {
            message.error(String(err));
        } finally {
            setAddingComment(false);
        }
    };

    const renderCommentsTab = () => {
        const sortedComments = [...comments].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        const hasDisplayedComments = sortedComments.length > 0 || pendingComments.length > 0;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {hasDisplayedComments ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {sortedComments.map((comment, idx) => (
                            <div
                                key={`${comment.created_at}-${idx}`}
                                style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, background: '#fafafa' }}
                            >
                                <Space size={8} style={{ marginBottom: 4 }} wrap>
                                    <Text strong>{comment.author || 'Unknown'}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {formatCommentTimestamp(comment.created_at)}
                                    </Text>
                                </Space>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{comment.text}</div>
                            </div>
                        ))}
                        {pendingComments.map((text, idx) => (
                            <div
                                key={`pending-${idx}`}
                                style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, background: '#fafafa' }}
                            >
                                <Space size={8} style={{ marginBottom: 4 }} wrap>
                                    <Text strong>Pending</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Saved when you create this entry
                                    </Text>
                                </Space>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
                )}

                {canEditCommentText && (
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Add comment</Text>
                        <TextArea
                            rows={3}
                            value={commentText}
                            disabled={addingComment}
                            placeholder="Add subjective feedback or comments"
                            onChange={(e) => setCommentText(e.target.value)}
                        />
                        <Button
                            type="primary"
                            style={{ marginTop: 8 }}
                            loading={addingComment}
                            disabled={!commentText.trim()}
                            onClick={handleAddComment}
                        >
                            Add comment
                        </Button>
                        {canQueueComment && (
                            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                Comments are saved with the ground truth when you create it.
                            </Text>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // --- Render per-task output panel ---

    const renderPerTaskOutputs = (taskIdx: number) => {
        const pt = perTaskList[taskIdx] ?? emptyPerTask();
        const taskId = pt.taskId || undefined;
        const sqlQueryAliases = ['GroundTruthPerTask.sql_queries', 'sql_queries'];
        const sqlOutputAliases = ['GroundTruthPerTask.sql_db_output', 'GroundTruthPerTask.sql_output', 'sql_db_output'];
        const taskNarrativeAliases = ['GroundTruthPerTask.narrative', 'task_narrative'];
        const vizAliases = ['GroundTruthPerTask.viz', 'GroundTruthViz', 'visualization', 'task_visualization'];
        return (
            <Tabs
                size="small"
                items={[
                    {
                        key: 'query',
                        label: renderRegenerationLabel('Query', sqlQueryAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(sqlQueryAliases, taskId)}>
                                {pt.sqlQueries.map((q, qIdx) => (
                                    <div key={`q-${qIdx}`} style={{ marginBottom: 8 }}>
                                        <Space align="start" style={{ width: '100%' }}>
                                            <Text strong>Query {qIdx + 1}</Text>
                                            <Button size="small" danger onClick={() => handleRemoveSqlQuery(taskIdx, qIdx)}>Remove</Button>
                                        </Space>
                                        <TextArea
                                            rows={2}
                                            value={q}
                                            onChange={(e) => handleSqlQueryChange(taskIdx, qIdx, e.target.value)}
                                            style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}
                                            placeholder="SELECT ... FROM ..."
                                        />
                                    </div>
                                ))}
                                <Button type="dashed" size="small" onClick={() => handleAddSqlQuery(taskIdx)}>
                                    + Add SQL query
                                </Button>
                            </div>
                        ),
                    },
                    {
                        key: 'table',
                        label: renderRegenerationLabel('Table', sqlOutputAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(sqlOutputAliases, taskId)}>
                                {pt.sqlTables.map((table, tIdx) => (
                                    <div key={`t-${tIdx}`} style={{ marginBottom: 16 }}>
                                        <Space align="start" style={{ width: '100%', marginBottom: 4 }}>
                                            <Text strong>Table {tIdx + 1}</Text>
                                            <Button size="small" danger onClick={() => handleRemoveTable(taskIdx, tIdx)}>Remove</Button>
                                        </Space>
                                        {table.columns?.length > 0 && table.rows?.length > 0 ? (
                                            <SkillStudioEditableTable
                                                value={table}
                                                onChange={(updated) => handleTableEdit(taskIdx, tIdx, updated)}
                                            />
                                        ) : (
                                            <div>
                                                <TextArea
                                                    rows={3}
                                                    placeholder="Paste tab-separated data from Excel (first row = headers)"
                                                    value={pt.pasteTexts[tIdx] ?? ''}
                                                    onChange={(e) => handlePasteTextChange(taskIdx, tIdx, e.target.value)}
                                                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                                                />
                                                <Button
                                                    size="small"
                                                    icon={<TableOutlined />}
                                                    style={{ marginTop: 4 }}
                                                    onClick={() => handleParseTable(taskIdx, tIdx, pt.pasteTexts[tIdx] ?? '')}
                                                >
                                                    Parse
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => handleAddTable(taskIdx)}>
                                    Add table
                                </Button>
                            </div>
                        ),
                    },
                    {
                        key: 'narrative',
                        label: renderRegenerationLabel('Narrative', taskNarrativeAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(taskNarrativeAliases, taskId)}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Overview</Text>
                                <TextArea
                                    rows={3}
                                    placeholder="Narrative overview for this task"
                                    value={pt.narrative}
                                    onChange={(e) => updatePerTask(taskIdx, { narrative: e.target.value })}
                                />
                                <Text strong style={{ display: 'block', marginTop: 8, marginBottom: 4 }}>Key Takeaways (one per line)</Text>
                                <TextArea
                                    rows={3}
                                    placeholder="One takeaway per line"
                                    value={pt.keyTakeaways}
                                    onChange={(e) => updatePerTask(taskIdx, { keyTakeaways: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'assumptions',
                        label: renderRegenerationLabel('Assumptions', taskNarrativeAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(taskNarrativeAliases, taskId)}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Assumptions (one per line)</Text>
                                <TextArea
                                    rows={5}
                                    placeholder="One assumption per line"
                                    value={pt.assumptions}
                                    onChange={(e) => updatePerTask(taskIdx, { assumptions: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'formulae',
                        label: renderRegenerationLabel('Formulae', taskNarrativeAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(taskNarrativeAliases, taskId)}>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Formulae (one per line)</Text>
                                <TextArea
                                    rows={5}
                                    placeholder="One formula per line"
                                    value={pt.formulae}
                                    onChange={(e) => updatePerTask(taskIdx, { formulae: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'viz',
                        label: renderRegenerationLabel('Visualization', vizAliases, taskId),
                        children: (
                            <div style={getFieldHighlightStyle(vizAliases, taskId)}>
                                <Space style={{ marginBottom: 8 }}>
                                    <Select
                                        allowClear
                                        placeholder="Chart type"
                                        style={{ width: 180 }}
                                        value={pt.chartType || undefined}
                                        onChange={(val) => updatePerTask(taskIdx, { chartType: val ?? '' })}
                                        options={CHART_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                                    />
                                    <Input
                                        placeholder="Chart title"
                                        style={{ width: 260 }}
                                        value={pt.chartTitle}
                                        onChange={(e) => updatePerTask(taskIdx, { chartTitle: e.target.value })}
                                    />
                                </Space>
                                {(pt.vizRaw || pt.chartType) && (
                                    <div style={{ marginTop: 8 }}>
                                        <SkillStudioVizPreview
                                            chartType={pt.chartType}
                                            chartTitle={pt.chartTitle}
                                            vizData={pt.vizRaw ?? undefined}
                                            sqlOutput={pt.sqlTables[0]}
                                        />
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        );
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onClose}
            width={900}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={handleSave}>
                        {mode === 'from_chat' ? 'Create Whole-Flow GT' : 'Save'}
                    </Button>
                </Space>
            }
        >
            <Spin spinning={saving}>
                <Form form={form} layout="vertical" requiredMark="optional" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => {
                            const next = key as 'ground-truth' | 'fixtures' | 'comments';
                            setActiveTab(next);
                            if (next === 'fixtures' && !fixturesTabVisited) {
                                setFixturesTabVisited(true);
                                void loadFixturesForForm();
                            }
                        }}
                        items={[
                            {
                                key: 'ground-truth',
                                label: 'Ground Truth',
                                children: (
                                    <>
                    {renderRegenerationSummary()}

                    <Form.Item
                        name="question_set"
                        label="Question Set"
                        rules={[{
                            required: true,
                            message: 'Required',
                            validator: (_, value) =>
                                Array.isArray(value) && value.length > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Required')),
                        }]}
                    >
                        <Select
                            showSearch allowClear mode="tags" maxCount={1}
                            placeholder="Select or type a new set name"
                            options={questionSets.map((s) => ({ value: s.question_set, label: `${s.question_set} (${s.count})` }))}
                            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>

                    <Form.Item name="question" label="Question" rules={[{ required: true, message: 'Required' }]}>
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item
                        name="intent"
                        label={renderRegenerationLabel('Intent', ['GroundTruthIntent'])}
                        style={getFieldHighlightStyle(['GroundTruthIntent'])}
                    >
                        <Select allowClear placeholder="Select intent" options={INTENT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                    </Form.Item>

                    <Form.Item
                        name="refine"
                        label={renderRegenerationLabel('Refine Output (optional)', ['GroundTruthRefine'])}
                        style={getFieldHighlightStyle(['GroundTruthRefine'])}
                    >
                        <TextArea rows={3} placeholder="Refine output when provided by the backend" />
                    </Form.Item>

                    <Form.Item
                        name="tasks"
                        label={renderRegenerationLabel('Tasks', ['GroundTruthTasks'])}
                        style={getFieldHighlightStyle(['GroundTruthTasks'])}
                    >
                        <Select mode="tags" placeholder="Add tasks (e.g. data_exploration)" />
                    </Form.Item>

                    {/* Plan: task + sub_query pairs */}
                    <Divider orientation="left" plain>
                        {renderRegenerationLabel('Plan', ['GroundTruthPlan'])}
                    </Divider>

                    <div style={{ marginBottom: 8, ...getFieldHighlightStyle(['GroundTruthPlan']) }}>
                        <Select
                            allowClear showSearch
                            placeholder="Select branch to load task names"
                            style={{ width: 300 }}
                            value={selectedTenant || undefined}
                            onChange={(val) => {
                                const tenant = val ?? '';
                                setSelectedTenant(tenant);
                                onTenantChange(tenant);
                            }}
                            options={tenants.map((t) => ({ value: t.name, label: t.name }))}
                        />
                    </div>

                    <Form.List name="plan">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...rest }) => (
                                    <Space key={key} align="start" style={{ display: 'flex', marginBottom: 4 }}>
                                        <Form.Item {...rest} name={[name, 'task_id']} hidden>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item {...rest} name={[name, 'task']} style={{ marginBottom: 0, minWidth: 220 }}>
                                            <Select
                                                showSearch allowClear
                                                placeholder="Task name" style={{ width: 220 }}
                                                options={taskNameOptions}
                                                onChange={(val) => handlePlanTaskChange(name, val ?? '')}
                                                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                        <Form.Item {...rest} name={[name, 'sub_query']} style={{ marginBottom: 0 }}>
                                            <Input placeholder="Sub-query" style={{ width: 420 }} />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => handleRemovePlanStep(remove, name)} />
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => handleAddPlanStep(add)} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                                    Add plan step
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <Divider orientation="left" plain>
                        {renderRegenerationLabel('Overall Narrative', ['GroundTruthNarrative', 'GroundTruthSummary'])}
                    </Divider>

                    <Form.Item
                        name="narrative_overview"
                        label={renderRegenerationLabel('Overview', ['GroundTruthNarrative', 'GroundTruthSummary'])}
                        style={getFieldHighlightStyle(['GroundTruthNarrative', 'GroundTruthSummary'])}
                    >
                        <TextArea rows={3} placeholder="Final-response overview" />
                    </Form.Item>

                    <Form.Item
                        name="narrative_key_takeaways"
                        label={renderRegenerationLabel('Key Takeaways (one per line)', ['GroundTruthNarrative', 'GroundTruthSummary'])}
                        style={getFieldHighlightStyle(['GroundTruthNarrative', 'GroundTruthSummary'])}
                    >
                        <TextArea rows={3} placeholder="One takeaway per line" />
                    </Form.Item>

                    <Divider orientation="left" plain>HITL Replay Config</Divider>

                    {renderHitlTrace()}

                    <Form.List name="hitl">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...rest }) => (
                                    <div key={key} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                                        <Space align="start" wrap style={{ width: '100%' }}>
                                            <Form.Item
                                                {...rest}
                                                name={[name, 'name']}
                                                label="Agent"
                                                style={{ marginBottom: 8 }}
                                                rules={[{ required: true, message: 'Required' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    placeholder="Select agent"
                                                    style={{ width: 180 }}
                                                    options={HITL_AGENT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                                                />
                                            </Form.Item>
                                            <Form.Item {...rest} name={[name, 'expected']} label="Expected" style={{ marginBottom: 8 }}>
                                                <Select
                                                    allowClear
                                                    placeholder="Expected?"
                                                    style={{ width: 130 }}
                                                    options={[
                                                        { value: true, label: 'True' },
                                                        { value: false, label: 'False' },
                                                    ]}
                                                />
                                            </Form.Item>
                                            <Form.Item {...rest} name={[name, 'should_use_value']} label="Should use value" style={{ marginBottom: 8 }}>
                                                <Input placeholder="Brand A" style={{ width: 180 }} />
                                            </Form.Item>
                                            <Form.Item {...rest} name={[name, 'should_complete']} label="Should complete" style={{ marginBottom: 8 }}>
                                                <Select
                                                    allowClear
                                                    placeholder="Complete?"
                                                    style={{ width: 130 }}
                                                    options={[
                                                        { value: true, label: 'True' },
                                                        { value: false, label: 'False' },
                                                    ]}
                                                />
                                            </Form.Item>
                                            <Button danger size="small" onClick={() => remove(name)} style={{ marginTop: 30 }}>
                                                Remove
                                            </Button>
                                        </Space>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'scripted_response']}
                                            label="Scripted response(s) (one per line)"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <TextArea rows={2} placeholder="One scripted response per line" />
                                        </Form.Item>
                                    </div>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                                    Add HITL validation
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <Form.Item name="status" label="Status">
                        <Select options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                    </Form.Item>

                    {/* Per-task outputs section */}
                    <Divider orientation="left" plain>
                        {renderRegenerationLabel('Per-Task Outputs', ['GroundTruthPerTask'])}
                    </Divider>

                    <Collapse
                        size="small"
                        accordion
                        items={perTaskList.map((pt, idx) => ({
                            key: String(idx),
                            label: (
                                <Space>
                                    <span>{pt.task || `Task ${idx + 1}`}{pt.subQuery ? ` — ${pt.subQuery}` : ''}</span>
                                    {getFieldRegenerationState(['GroundTruthPerTask'], pt.taskId || undefined).regenerated && (
                                        <Tag color={isRegenerationPreview ? 'processing' : 'success'}>
                                            {isRegenerationPreview ? 'Would regenerate' : 'Regenerated'}
                                        </Tag>
                                    )}
                                    {getFieldRegenerationState(['GroundTruthPerTask'], pt.taskId || undefined).stale && <Tag color="warning">Needs review</Tag>}
                                    <MinusCircleOutlined
                                        onClick={(e) => { e.stopPropagation(); removePerTask(idx); }}
                                        style={{ color: '#ff4d4f' }}
                                    />
                                </Space>
                            ),
                            children: (
                                <div>
                                    <Space style={{ marginBottom: 8 }}>
                                        <Select
                                            showSearch allowClear
                                            placeholder="Task name" style={{ width: 220 }}
                                            value={pt.task || undefined}
                                            onChange={(val) => handlePerTaskTaskChange(idx, val ?? '')}
                                            options={taskNameOptions}
                                            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                                        />
                                        <Input
                                            placeholder="Sub-query"
                                            style={{ width: 360 }}
                                            value={pt.subQuery}
                                            onChange={(e) => updatePerTask(idx, { subQuery: e.target.value })}
                                        />
                                    </Space>
                                    {renderPerTaskOutputs(idx)}
                                </div>
                            ),
                        }))}
                    />
                    <Button type="dashed" onClick={addPerTask} icon={<PlusOutlined />} style={{ marginTop: 8, marginBottom: 16 }}>
                        Add task output
                    </Button>
                                    </>
                                ),
                            },
                            {
                                key: 'fixtures',
                                label: 'Fixtures',
                                children: renderFixturesTab(),
                            },
                            {
                                key: 'comments',
                                label: 'Subjective Feedback / Comments',
                                children: renderCommentsTab(),
                            },
                        ]}
                    />
                </Form>
            </Spin>
        </Modal>
    );
};

export default SkillStudioEvalGroundTruthForm;
